import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { HealthProPhysicians, HealthProReportDiagnosis } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { FormRowEditable } from "../../../components/ui-kit/form/form-row-editable";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  healthProPatientDiagnoses,
  healthProPhysicians,
} from "../../../constants/enum-to-text";
import { hasMonitoringAccess } from "../../../constants/permissions";
import { assert } from "../../../utils/utils";
import { AddHealthProDialog } from "./add-health-pro-dialog";

export const HealthProDetails = observer(function HealthProDetails(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const form = patient.healthProDetailsForm;
  assert(patient.data, "Patient data is missing");

  const [showAddDialog, setShowAddDialog] = useState(false);
  const updateHealthPro = (editedField: Record<string, any>) => {
    void patient.updatePatientField(editedField);
  };
  const insurancePolicyDetails = patient.data.insurance_policy_details as
    | {
        healthpro_id?: string;
        healthpro_diagnosis?: HealthProReportDiagnosis;
        healthpro_physician?: HealthProPhysicians;
      }
    | undefined;

  return (
    <div>
      {patient.isHealthProPatient && (
        <>
          <FormTable big>
            <FormRowEditable
              label={t`HealthPro ID`}
              form={form}
              attr="insurance_policy_details"
              model={patient.data}
              viewMode={
                <a
                  target="_blank"
                  href={`https://zona.healthpro.sk/patients/detail/${insurancePolicyDetails?.healthpro_id}/`}
                  rel="noreferrer"
                >
                  {insurancePolicyDetails?.healthpro_id}
                </a>
              }
              editMode={(editedField) => (
                <TextInput
                  attr="healthpro_id"
                  model={editedField.insurance_policy_details}
                  width={100}
                />
              )}
              onChange={updateHealthPro}
            />
            <FormRowEditable
              label={t`HealthPro main diagnosis`}
              form={form}
              attr="insurance_policy_details"
              model={patient.data}
              viewMode={
                insurancePolicyDetails?.healthpro_diagnosis !== undefined
                  ? healthProPatientDiagnoses[
                      insurancePolicyDetails.healthpro_diagnosis
                    ]
                  : undefined
              }
              editMode={(editedField) => (
                <Select
                  attr="healthpro_diagnosis"
                  model={editedField.insurance_policy_details}
                  values={healthProPatientDiagnoses}
                />
              )}
              onChange={updateHealthPro}
            />
            <FormRowEditable
              label={t`HealthPro physician`}
              form={form}
              attr="insurance_policy_details"
              model={patient.data}
              viewMode={
                insurancePolicyDetails?.healthpro_physician !== undefined
                  ? healthProPhysicians[
                      insurancePolicyDetails?.healthpro_physician
                    ]
                  : undefined
              }
              editMode={(editedField) => (
                <Select
                  attr="healthpro_physician"
                  model={editedField.insurance_policy_details}
                  values={healthProPhysicians}
                />
              )}
              onChange={updateHealthPro}
            />
          </FormTable>
          <br className="h-10" />
          {hasMonitoringAccess(patient.api.permissions) && (
            <Button
              destructive
              onClick={async () => {
                await patient.clearHealthProDetails();
              }}
            >
              <Trans>Clear HealthPro details</Trans>
            </Button>
          )}
        </>
      )}
      {!patient.isHealthProPatient &&
        hasMonitoringAccess(patient.api.permissions) && (
          <>
            <Button onClick={() => setShowAddDialog(true)}>
              <Trans>Add HealthPro details</Trans>
            </Button>
            {showAddDialog && (
              <AddHealthProDialog
                app={props.app}
                onClose={() => setShowAddDialog(false)}
              />
            )}
          </>
        )}
    </div>
  );
});
